import { Theming } from '~schemas/settings';

/**
 * Default light theming values used as the baseline for light branding.
 * Individual values are overridden at runtime by the /v2/branding API response.
 */
export const DEFAULT_LIGHT_THEME: Theming = {
  fonts: {
    heading: { family: 'Poppins' },
    body: { family: 'Inter' },
  },
  surface: {
    primary: '#FFFFFF',
    primary_support: '#EAEDF1',
    primary_support_soft: '#F3F5F7',
    neutral: '#DBE1E7',
    focus: '#3A79FF',
    positive: '#3BD66F',
    positive_support: '#EDFCF2',
    warning: '#F5B71E',
    warning_support: '#FEF8E8',
    error: '#FF4667',
    error_support: '#FFEBEF',
    shadow: '#09218719',
    hover: '#00000019',
    video_overlay_button: '#00000019',
    modal_overlay: '#FFFFFFEB',
  },
  border: {
    primary: '#DBE1E7',
    disabled: '#EAEDF1',
    hover: '#B7C6D2',
  },
  text: {
    heading: '#092187',
    body: '#092187',
    secondary: '#7186A0',
    focus: '#FFFFFF',
    disabled: '#DBE1E7',
    overlay_disabled: '#FFFFFF33',
  },
};

/**
 * Default dark theming values used as the baseline for dark branding.
 * Individual values are overridden at runtime by the /v2/branding API response.
 */
export const DEFAULT_DARK_THEME: Theming = {
  fonts: {
    heading: { family: 'Poppins' },
    body: { family: 'Inter' },
  },
  surface: {
    primary: '#181E25',
    primary_support: '#0D1014',
    primary_support_soft: '#F3F5F7',
    neutral: '#2A323E',
    focus: '#3A79FF',
    positive: '#3BD66F',
    positive_support: '#0B411E',
    warning: '#F5B71E',
    warning_support: '#493503',
    error: '#FF4667',
    error_support: '#4C000E',
    shadow: '#00000019',
    hover: '#FFFFFF19',
    video_overlay_button: '#00000019',
    modal_overlay: '#000000EB',
  },
  border: {
    primary: '#3B4C5D',
    disabled: '#2A323E',
    hover: '#4A5E70',
  },
  text: {
    heading: '#FFFFFF',
    body: '#FFFFFF',
    secondary: '#7186A0',
    focus: '#FFFFFF',
    disabled: '#3B4C5D',
    overlay_disabled: '#FFFFFF33',
  },
};

/**
 * Theme token → CSS custom property.
 *
 * Single source of truth for the token names: `applyTheming` writes the branding
 * response onto these, and component tokens reference them by name. The literal
 * defaults for each one live in `src/index.css`.
 */
export const THEME_TOKEN_CSS_VARS = {
  fonts: {
    heading: '--font-heading',
    body: '--font-body',
  },
  surface: {
    primary: '--color-surface-primary',
    primary_support: '--color-surface-primary-support',
    primary_support_soft: '--color-surface-primary-support-soft',
    neutral: '--color-surface-neutral',
    focus: '--color-surface-focus',
    positive: '--color-surface-positive',
    positive_support: '--color-surface-positive-support',
    warning: '--color-surface-warning',
    warning_support: '--color-surface-warning-support',
    error: '--color-surface-error',
    error_support: '--color-surface-error-support',
    shadow: '--color-surface-shadow',
    hover: '--color-surface-hover',
    video_overlay_button: '--color-surface-video-overlay-button',
    modal_overlay: '--color-surface-modal-overlay',
  },
  border: {
    primary: '--color-border-primary',
    disabled: '--color-border-disabled',
    hover: '--color-border-hover',
  },
  text: {
    heading: '--color-text-heading',
    body: '--color-text-body',
    secondary: '--color-text-secondary',
    focus: '--color-text-focus',
    disabled: '--color-text-disabled',
    overlay_disabled: '--color-text-overlay-disabled',
  },
} as const satisfies Record<keyof Theming, Record<string, `--${string}`>>;

/** The theme sections a component token may point at. */
type ThemeColourSection = 'surface' | 'border' | 'text';

/**
 * Dotted name of a colour theme token, e.g. `surface.primary_support`. Fonts are
 * deliberately excluded: component tokens are colours.
 */
export type ThemeColourToken = {
  [S in ThemeColourSection]: `${S}.${Extract<keyof (typeof THEME_TOKEN_CSS_VARS)[S], string>}`;
}[ThemeColourSection];

/** `surface.primary_support` → `--color-surface-primary-support`. */
export function themeColourCssVar(token: ThemeColourToken): string {
  const [section, key] = token.split('.') as [ThemeColourSection, string];
  return (THEME_TOKEN_CSS_VARS[section] as Record<string, string>)[key];
}
